/*
 * @license
 * Copyright© Celligence International, LLC - 2023
 *
 * This software program is protected by copyright laws and is the property of
 * Celligence International, LLC (Celligence). Any unauthorized reproduction,
 * distribution, modification, or public display of this program, without the
 * explicit permission of Celligence is strictly prohibited. If you have gained
 * access to this program without the expressed approval of Celligence report
 * the incident to legal@celligence.com.
 *
 */
import React, { useEffect, useState, Suspense, lazy } from "react";
import { useParams } from "react-router-dom";
import Container from "react-bootstrap/Container";
import { HelmetProvider } from "react-helmet-async";
import MetaTags from "../MetaTags";
import SpinnerLoader from "../SpinnerLoader";
import axios from "axios";
import { isMobileDevice, isMobileNotTabletDevice, hasEventExpired } from "../Utils";

const PreEventTemplates = lazy(() => import("../PreEventTemplates"));
const PostEventTemplates = lazy(() => import("../PostEventTemplates"));

export default function UserEventHomePage() {
    const { username } = useParams();
    const correctedUserName = username?.toLowerCase();
    const { eventName } = useParams();
    const correctedEventName = eventName.toLowerCase();
    const [showPostEventPage, setShowPostEventPage] = useState(false);
    const [eventPageData, setEventPageData] = useState(null);
    const [preEventData, setPreEventData] = useState(null);
    const [postEventData, setPostEventData] = useState(null);
    const [metaTagsData, setMetaTagsData] = useState(null);
    const [isMobile, setIsMobile] = useState(isMobileDevice());
    const [isMobileNotTablet, setIsMobileNotTablet] = useState(isMobileNotTabletDevice());

    useEffect(() => {
        /**
         * @author Pratik Kajrolkar
         * @date 06/05/2025
         *
         * The below function is used to fetch the event data JSON from the AWS S3 bucket location.
         * @param {String} objectName the json object which will fetch from S3 bucket
         * @returns JSON Object of the requested file
        */
        const fetchData = async () => {
            try {
                // Add timestamp or version parameter to bypass cache
                const cacheBuster = new Date().getTime(); // or use a version number
                const url = `${process.env.REACT_APP_EVENTS_PAGE_JSON}${correctedUserName}/${correctedEventName}/${correctedEventName}.json?v=${cacheBuster}`;
                
                const response = await axios.get(url);
                if (response.data) {
                    const eventData = response.data;
                    setMetaTagsData(eventData.metaTags);
                    setEventPageData(eventData);
                    // Automatically fall back to the Post Event page once the event is more
                    // than 24 hours past its scheduled date/time, so expired events stop
                    // accepting registrations without needing showPostEventPage to be set
                    // manually. An explicit showPostEventPage:true still takes effect early.
                    // Only auto-expire when post-event content actually exists, so events
                    // missing that data don't get switched to a broken/empty page.
                    setShowPostEventPage(eventData.showPostEventPage || (Boolean(eventData.postEvent) && hasEventExpired(eventData)));
                    setPreEventData(eventData.preEvent);
                    setPostEventData(eventData.postEvent);
                }
                else{
                    window.location.href = "https://www.angelai.com/";
                }
            } catch (e) {
                console.log(e);
                window.location.href = "https://www.angelai.com/";
            }
        };

        fetchData();
    }, [correctedUserName, correctedEventName]);

    useEffect(() => {
        function handleResize() {
            setIsMobile(isMobileDevice());
            setIsMobileNotTablet(isMobileNotTabletDevice());
        }

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    return (
        <HelmetProvider>
            <Container fluid className="ps-0 pe-0">
                {eventPageData ? 
                    <>
                        <MetaTags eventCreatedUnderDomain={eventPageData.eventCreatedUnderDomain} metaTagsData={metaTagsData} />
                        <Suspense fallback={<SpinnerLoader />}>
                            {showPostEventPage ? 
                                <PostEventTemplates 
                                    eventCreatedUnderDomain={eventPageData.eventCreatedUnderDomain}
                                    eventPageData={eventPageData} 
                                    preEventData={preEventData}
                                    postEventData={postEventData} 
                                    isMobile={isMobile}
                                    isMobileNotTablet={isMobileNotTablet}
                                />
                            :
                                <PreEventTemplates 
                                    eventCreatedUnderDomain={eventPageData.eventCreatedUnderDomain}
                                    eventPageData={eventPageData} 
                                    preEventData={preEventData} 
                                    isMobile={isMobile}
                                    isMobileNotTablet={isMobileNotTablet}
                                />
                            }
                        </Suspense>
                    </>
                : 
                    <SpinnerLoader />
                }
            </Container>
        </HelmetProvider>
    );
};